import { ApplicationInsights } from '@microsoft/applicationinsights-web';

import env from '../env';
import { applyBrowserTelemetryHygiene } from './telemetryHygiene';

/**
 * Browser-side Application Insights instrumentation for OnlineForms2.
 *
 * Configured via runtime environment (window.__ENV__), populated by the Docker
 * entrypoint from FUSION_OF_* container variables on Azure App Service.
 *
 * If FUSION_OF_APPLICATIONINSIGHTS_CONNECTION_STRING is empty (the local/dev
 * default), this module becomes a no-op so developers never accidentally ship
 * telemetry to a Production resource from a workstation.
 *
 * Telemetry hygiene notes:
 * - Default SDK behaviour records request URL, status, and duration; it does
 *   NOT capture request or response bodies, so GraphQL variables and form
 *   answers are never sent. Do not enable any *Body* tracking option here
 *   without a privacy review.
 * - Every URL-like telemetry value has its complete query and fragment removed
 *   before transmission. This includes initial/automatic SPA page views and
 *   dependency URLs, so receipt capabilities never leave the browser in telemetry.
 * - cloud_RoleName is set explicitly so OnlineForms2 is filterable as a
 *   distinct app in shared Production App Insights queries.
 */

const connectionString = env.FUSION_OF_APPLICATIONINSIGHTS_CONNECTION_STRING;
const roleName = env.FUSION_OF_APPLICATIONINSIGHTS_ROLE_NAME;

let appInsights: ApplicationInsights | null = null;

export function initialiseTelemetry(): ApplicationInsights | null {
  if (!connectionString) {
    // No connection string configured: stay silent. This is the expected
    // path in local dev and in any environment where browser telemetry is
    // intentionally disabled.
    return null;
  }

  if (appInsights) {
    return appInsights;
  }

  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        // SPA route changes are sent as page views.
        enableAutoRouteTracking: true,
        // Capture XHR + fetch as dependencies for end-to-end correlation.
        disableAjaxTracking: false,
        disableFetchTracking: false,
        // Send W3C trace headers so requests can be correlated with the API
        // server-side traces. The API CORS policy must allow these headers
        // (traceparent, request-id, request-context); if it does not, the
        // browser will block the request and this can be set to false.
        enableCorsCorrelation: true,
        // Anonymous user/session cookies are required for App Insights to
        // group page views into sessions. They contain only random IDs.
        disableCookiesUsage: false,
      },
    });

    appInsights.loadAppInsights();

    // Stable cloud role so OnlineForms2 telemetry is identifiable in shared
    // Production App Insights queries (e.g. cloud_RoleName == 'prod-rcop-onlineforms2-browser').
    appInsights.addTelemetryInitializer((envelope) =>
      applyBrowserTelemetryHygiene(envelope, roleName),
    );

    appInsights.trackPageView();

    // Single, low-noise startup signal. Do NOT log the connection string.
    // eslint-disable-next-line no-console
    console.info('[telemetry] Application Insights initialised', { roleName });
  } catch (error) {
    // Telemetry must never break the app. Log and swallow.
    appInsights = null;
    // eslint-disable-next-line no-console
    console.warn('[telemetry] Application Insights failed to initialise', error);
  }

  return appInsights;
}

export function getAppInsights(): ApplicationInsights | null {
  return appInsights;
}
